import { ServiceValues } from "@components/ServiceValuesModal";
import { ProjectService } from "../../types";

export interface GroupUpdateItem {
  id: string;
  type?: string;
}

type MutationResult = { data?: unknown; error?: unknown };
type UpdateMutation = (args: {
  id: string;
  body: any;
}) => Promise<MutationResult>;

// The JSON:API body that applies the filled-in values to one item. Blank
// fields are left out; a subservice only takes quantity, hours and resource.
// Returns null when none of the values apply to the item.
export const buildGroupUpdate = (
  item: GroupUpdateItem,
  values: ServiceValues,
  projectId: string
) => {
  const isService = item.type === "project-services";
  const attributes: Record<string, number> = {};
  const relationships: Record<string, { data: { id: any; type: string } }> = {};

  if (values.quantity !== undefined) attributes.quantity = values.quantity;
  if (values.hours !== undefined) attributes["override-hours"] = values.hours;
  if (values.resource)
    relationships["project-resource"] = {
      data: { id: values.resource, type: "project-resources" },
    };
  if (isService) {
    if (values.phase)
      relationships["project-phase"] = {
        data: { id: Number(values.phase), type: "project-phases" },
      };
    if (values.serviceCategory)
      relationships["service-category"] = {
        data: {
          id: Number(values.serviceCategory),
          type: "service-categories",
        },
      };
  }

  if (!Object.keys(attributes).length && !Object.keys(relationships).length)
    return null;

  if (isService)
    relationships.project = {
      data: { id: Number(projectId), type: "projects" },
    };

  return {
    data: {
      id: Number(item.id),
      type: item.type,
      attributes,
      relationships,
    },
  };
};

// Applies the values to each item one at a time, like bulk delete, and
// returns how many were updated and which ones failed.
export const applyGroupUpdate = async <T extends GroupUpdateItem>(
  items: T[],
  values: ServiceValues,
  projectId: string,
  mutations: { updateService: UpdateMutation; updateSubservice: UpdateMutation }
) => {
  let updated = 0;
  const failed: T[] = [];
  for (const item of items) {
    const body = buildGroupUpdate(item, values, projectId);
    if (!body) continue;
    const update =
      item.type === "project-services"
        ? mutations.updateService
        : mutations.updateSubservice;
    try {
      const res = await update({ id: String(item.id), body });
      if (res?.error) failed.push(item);
      else updated++;
    } catch {
      failed.push(item);
    }
  }
  return { updated, failed };
};

// Services and subservices can share an id, so a selection key includes the type.
export const selectionKey = (item: GroupUpdateItem) => `${item.type}:${item.id}`;

// Re-checks the given items on a freshly fetched service list, so a failed
// group update leaves those items selected for a retry.
export const restoreSelection = (
  services: ProjectService[],
  keys: string[]
): ProjectService[] => {
  if (!keys.length) return services;
  const isChecked = (item: GroupUpdateItem) =>
    keys.includes(selectionKey(item));
  return services.map((s) => ({
    ...s,
    checked: isChecked(s),
    subservices: s.subservices?.map((sub) => ({
      ...sub,
      checked: isChecked(sub),
    })),
  }));
};
